import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useBotGuard } from "@/components/BotTrap";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const { trap, guard } = useBotGuard();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/app";

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email.trim(), password, guard());
      toast.success("Welcome back");
      nav(from, { replace: true });
    } catch (e) {
      if (e?.requiresVerification) {
        toast.info(e.message || "Verify your email to continue");
        nav("/verify-email", {
          replace: true,
          state: {
            email: e.email || email.trim().toLowerCase(),
            retryAfter: e.retryAfter,
          },
        });
        return;
      }
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-10 border-r border-[var(--border)] bg-white">
        <Link to="/" className="flex items-center gap-2" data-testid="link-brand">
          <div className="w-8 h-8 rounded-md bg-[var(--fg)] text-white grid place-items-center font-bold">IF</div>
          <span className="font-semibold tracking-tight">InvoiceForge</span>
        </Link>
        <div>
          <div className="overline mb-4">Sign in to</div>
          <h1 className="text-5xl leading-none tracking-tight font-medium">
            Your billing <br /><span className="font-serif italic">workspace</span>
          </h1>
          <p className="mt-6 text-gray-600 max-w-md">Access your invoices, receipts, POs, and every PDF you've ever generated — all in one place.</p>
        </div>
        <div className="overline">Custom JWT · Secure · Fast</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={onSubmit} className="w-full max-w-sm">
          {trap}
          <div className="overline mb-3">Sign in</div>
          <h2 className="text-3xl font-medium tracking-tight mb-8">Welcome back.</h2>
          <label className="block mb-4">
            <div className="text-xs text-gray-600 mb-1.5">Email</div>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full h-11 px-3 border border-[var(--border)] rounded-md bg-white focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 outline-none"
              data-testid="login-email"
            />
          </label>
          <label className="block mb-6">
            <div className="text-xs text-gray-600 mb-1.5">Password</div>
            <input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full h-11 px-3 border border-[var(--border)] rounded-md bg-white focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 outline-none"
              data-testid="login-password"
            />
          </label>
          {err && <div className="mb-4 text-sm text-red-600" data-testid="login-error">{err}</div>}
          <button className="w-full btn-primary" disabled={busy} data-testid="login-submit">
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <div className="mt-6 text-sm text-gray-600">
            No account yet? <Link to="/register" className="underline underline-offset-4 text-[var(--fg)]" data-testid="link-register">Create one</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
